<script>
    import { Square as SquareIcon, SquareCheck as SquareCheckIcon } from 'lucide-svelte';
    import { ValueService } from '../../services/ValueService.js';

    /**
     * @type {{
     *     name?: string | null,
     *     id?: string | null,
     *     label?: string | null,
     *     description?: string | null,
     *     size?: number | string,
     *     autofocus?: boolean,
     *     required?: boolean,
     *     value?: boolean,
     *     thisRef?: HTMLInputElement | null,
     *     [key: string]: any,
     * }}
     */
    let {
        name = null,
        id = null,
        label = null,
        description = null,
        size = 22,
        autofocus = false,
        required = false,
        value = $bindable(false),
        thisRef = $bindable(null),
        ...restProps
    } = $props();

    const useSize = $derived(ValueService.toUInt(size, 22));

    const useName = $derived(ValueService.toStringSingleLine(name));
    const useId = $derived(
        ValueService.hasValue(id) ? ValueService.toStringSingleLine(id) : `ly-input-${crypto.randomUUID()}`
    );
    const useDescription = $derived(ValueService.toStringSingleLine(description));
    const useChecked = $derived(ValueService.toBool(value));

    /**
     * @type {{
     *     id: string,
     *     type: "checkbox",
     *     name?: string,
     *     autofocus: boolean,
     *     required: boolean,
     * }}
     */
    const inputProps = $derived({
        id: useId,
        type: /** @type {"checkbox"} */ ('checkbox'),
        ...(useName !== '' ? { name: useName } : {}),
        autofocus: ValueService.toBool(autofocus),
        required: ValueService.toBool(required),
    });

    /**
     * @param {Event & {currentTarget: EventTarget & HTMLInputElement}} event
     * @returns {void}
     */
    function onCheckboxChange(event) {
        value = event.currentTarget.checked;
    }
</script>

<div class="lyInput lyInputCheckbox">
    <div class="flex max-w-md items-center py-1">
        <input
            {...restProps}
            {...inputProps}
            checked={useChecked}
            onchange={onCheckboxChange}
            bind:this={thisRef}
            class="peer sr-only" />
        <label
            for={useId}
            class="peer-disabled:text-black-300 m-0 flex
               cursor-pointer items-center
               gap-2 rounded-sm
               text-(length:--dwc-input-font-size) leading-none
               select-none
               peer-focus-visible:ring
               peer-disabled:pointer-events-none">
            {#if useChecked}
                <SquareCheckIcon size={useSize} aria-hidden="true" class="lyInputIconChecked shrink-0" />
            {:else}
                <SquareIcon size={useSize} aria-hidden="true" class="lyInputIcon shrink-0" />
            {/if}
            {#if ValueService.isDefined(label)}
                <span>{ValueService.toStringSingleLine(label)}</span>
            {/if}
        </label>
    </div>
    {#if useDescription}
        <p class="text-black-300 m-0 block w-full max-w-md px-2 pr-1 pl-8 text-xs">{useDescription}</p>
    {/if}
</div>

<style>
    .lyInputCheckbox {
        --dwc-input-font-size: 1rem;
        :global(.lyInputIcon) {
            color: var(--color-black-500);
        }
        :global(.lyInputIconChecked) {
            color: var(--color-checked-500);
        }
        input:disabled + label :global(svg) {
            color: var(--color-black-300);
        }
    }
</style>
